import axios from 'axios';
import { Blog, BlogCreatePayload, BlogListItem, AdminUser, PaginatedResponse } from '../types';
import { getApiBaseUrl, handleApiError } from './api';

const API_BASE_URL = getApiBaseUrl();
const ADMIN_TOKEN_KEY = 'travelbaba_admin_token';

const blogApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const adminApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authStorage = {
  getToken: () => localStorage.getItem(ADMIN_TOKEN_KEY),
  setToken: (token: string) => localStorage.setItem(ADMIN_TOKEN_KEY, token),
  clearToken: () => localStorage.removeItem(ADMIN_TOKEN_KEY),
  isAuthenticated: () => !!localStorage.getItem(ADMIN_TOKEN_KEY),
};

export const getMediaUrl = (path?: string | null): string => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

export const blogsAPI = {
  list: async (page = 1, pageSize = 9, search?: string, tag?: string) => {
    try {
      const response = await blogApi.get<PaginatedResponse<BlogListItem>>('/api/v1/blogs/', {
        params: { page, page_size: pageSize, search, tag },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to fetch blogs');
    }
  },

  getBySlug: async (slug: string) => {
    try {
      const response = await blogApi.get<Blog>(`/api/v1/blogs/${encodeURIComponent(slug)}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to fetch blog');
    }
  },
};

export const adminAPI = {
  login: async (email: string, password: string) => {
    try {
      const response = await blogApi.post<{ access_token: string }>('/api/v1/admin/auth/login', {
        email,
        password,
      });
      authStorage.setToken(response.data.access_token);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Login failed');
    }
  },

  logout: () => {
    authStorage.clearToken();
  },

  me: async () => {
    try {
      const response = await adminApi.get<AdminUser>('/api/v1/admin/auth/me');
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to verify admin session');
    }
  },

  listBlogs: async (page = 1, pageSize = 20, status?: string, search?: string) => {
    try {
      const response = await adminApi.get<PaginatedResponse<BlogListItem>>('/api/v1/admin/blogs/', {
        params: { page, page_size: pageSize, status, search },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to fetch admin blogs');
    }
  },

  getBlog: async (id: string) => {
    try {
      const response = await adminApi.get<Blog>(`/api/v1/admin/blogs/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to fetch blog');
    }
  },

  createBlog: async (payload: BlogCreatePayload) => {
    try {
      const response = await adminApi.post<Blog>('/api/v1/admin/blogs/', payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to create blog');
    }
  },

  updateBlog: async (id: string, payload: Partial<BlogCreatePayload>) => {
    try {
      const response = await adminApi.put<Blog>(`/api/v1/admin/blogs/${id}`, payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to update blog');
    }
  },

  updateStatus: async (id: string, status: 'draft' | 'published') => {
    try {
      const response = await adminApi.patch<Blog>(`/api/v1/admin/blogs/${id}/status`, { status });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to update blog status');
    }
  },

  deleteBlog: async (id: string) => {
    try {
      await adminApi.delete(`/api/v1/admin/blogs/${id}`);
    } catch (error) {
      throw handleApiError(error, 'Failed to delete blog');
    }
  },

  uploadImage: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await adminApi.post<{ filename: string; url: string }>(
        '/api/v1/admin/upload/image',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to upload image');
    }
  },
};

export default blogApi;
